import { isPrivacyRequestType } from '../lib/privacyRequests';
import { createTimeoutSignal } from "./timeoutSignal";
import { getEmailError, normalizeEmail } from "../utils/email";
import { mergeCustomerInfo } from "../utils/customerInfo";

import { getVisitorId, LS_WEBCHAT_UUID } from "./visitorId";
import { trackEvent } from "../../redesign/lib/analytics.js";
import { readFirstTouch } from "../../redesign/lib/attribution.js";
import { buildLeadEvent } from "../../redesign/lib/lead-event.js";

const BASE = "/api/demo-request";
export { LS_WEBCHAT_UUID };

const RESERVED_PAYLOAD_KEYS = new Set(["currenturl", "current_url", "href", "useragent", "user_agent"]);
const CLICK_ID_KEYS = new Set(["gclid", "fbclid", "msclkid", "ttclid", "wbraid", "gbraid", "yclid"]);

function resolveCurrentUrl(metadata, payload) {
  if (typeof window !== "undefined" && window.location?.href) {
    return window.location.href;
  }
  const candidates = [
    metadata?.current_url,
    metadata?.currentUrl,
    metadata?.href,
    payload?.current_url,
    payload?.currentUrl,
    payload?.href,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

function resolveUserAgent(metadata, payload) {
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    return navigator.userAgent;
  }
  const candidates = [metadata?.user_agent, metadata?.userAgent, payload?.user_agent, payload?.userAgent];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

function compactValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (Array.isArray(value)) {
    const cleaned = value.map(compactValue).filter((item) => item !== null);
    return cleaned.length ? cleaned : null;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value)
      .map(([key, val]) => [key, compactValue(val)])
      .filter(([, val]) => val !== null);
    return entries.length ? Object.fromEntries(entries) : null;
  }
  return value;
}

function matchMediaValue(query) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  return window.matchMedia(query).matches;
}

function getColorSchemePreference() {
  const prefersDark = matchMediaValue("(prefers-color-scheme: dark)");
  if (prefersDark === true) return "dark";
  const prefersLight = matchMediaValue("(prefers-color-scheme: light)");
  if (prefersLight === true) return "light";
  if (prefersDark === false || prefersLight === false) return "no-preference";
  return null;
}

function getReducedMotionPreference() {
  const prefersReduce = matchMediaValue("(prefers-reduced-motion: reduce)");
  if (prefersReduce === true) return "reduce";
  const prefersNoPreference = matchMediaValue("(prefers-reduced-motion: no-preference)");
  if (prefersNoPreference === true) return "no-preference";
  if (prefersReduce === false) return "no-preference";
  return null;
}

function getContrastPreference() {
  const prefersMore = matchMediaValue("(prefers-contrast: more)");
  if (prefersMore === true) return "more";
  const prefersLess = matchMediaValue("(prefers-contrast: less)");
  if (prefersLess === true) return "less";
  const prefersNoPreference = matchMediaValue("(prefers-contrast: no-preference)");
  if (prefersNoPreference === true) return "no-preference";
  if (prefersMore === false || prefersLess === false) return "no-preference";
  return null;
}

function collectQueryPayload() {
  if (typeof window === "undefined") return null;
  const search = window.location?.search || "";
  if (!search) return null;
  const params = new URLSearchParams(search);
  const utm = {};
  const clickIds = {};
  params.forEach((value, key) => {
    const normalizedKey = String(key).toLowerCase();
    if (normalizedKey.startsWith("utm_")) {
      utm[normalizedKey] = value;
    }
    if (CLICK_ID_KEYS.has(normalizedKey)) {
      clickIds[normalizedKey] = value;
    }
  });
  const payload = {};
  if (Object.keys(utm).length) payload.utm = utm;
  if (Object.keys(clickIds).length) payload.clickIds = clickIds;
  return Object.keys(payload).length ? payload : null;
}

function getUserAgentData() {
  if (typeof navigator === "undefined" || !navigator.userAgentData) {
    return null;
  }
  const data = navigator.userAgentData;
  const brands = Array.isArray(data.brands)
    ? data.brands.map((brand) => `${brand.brand}/${brand.version}`).filter(Boolean)
    : null;
  const payload = {
    brands,
    mobile: typeof data.mobile === "boolean" ? data.mobile : null,
    platform: typeof data.platform === "string" ? data.platform : null,
  };
  return compactValue(payload);
}

function collectClientPayload() {
  if (typeof window === "undefined") return null;
  const nav = typeof navigator !== "undefined" ? navigator : null;
  const doc = typeof document !== "undefined" ? document : null;
  const screen = typeof window !== "undefined" ? window.screen : null;
  const connection = nav?.connection || nav?.mozConnection || nav?.webkitConnection;
  const payload = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    locale: nav?.language || null,
    languages: Array.isArray(nav?.languages) ? nav.languages : null,
    documentLang: doc?.documentElement?.lang || null,
    referrer: doc?.referrer || null,
    page: {
      path: window.location?.pathname || null,
      search: window.location?.search || null,
      hash: window.location?.hash || null,
      title: doc?.title || null,
    },
    screen: screen
      ? {
          w: screen.width,
          h: screen.height,
          colorDepth: screen.colorDepth,
          pixelRatio: window.devicePixelRatio,
        }
      : null,
    viewport: {
      w: window.innerWidth || doc?.documentElement?.clientWidth || null,
      h: window.innerHeight || doc?.documentElement?.clientHeight || null,
    },
    visualViewport: window.visualViewport
      ? {
          w: Math.round(window.visualViewport.width),
          h: Math.round(window.visualViewport.height),
          scale: window.visualViewport.scale || null,
        }
      : null,
    device: {
      platform: nav?.platform || null,
      vendor: nav?.vendor || null,
      maxTouchPoints: nav?.maxTouchPoints ?? null,
      hardwareConcurrency: nav?.hardwareConcurrency ?? null,
      deviceMemory: nav?.deviceMemory ?? null,
    },
    connection: connection
      ? {
          effectiveType: connection.effectiveType || null,
          downlink: connection.downlink ?? null,
          rtt: connection.rtt ?? null,
          saveData: connection.saveData ?? null,
        }
      : null,
    cookiesEnabled: nav?.cookieEnabled ?? null,
    doNotTrack: nav?.doNotTrack || null,
    preferences: {
      colorScheme: getColorSchemePreference(),
      reducedMotion: getReducedMotionPreference(),
      contrast: getContrastPreference(),
    },
    userAgentData: getUserAgentData(),
  };
  const queryPayload = collectQueryPayload();
  if (queryPayload) {
    Object.assign(payload, queryPayload);
  }
  try {
    const firstTouch = readFirstTouch({ storage: window.localStorage, sessionStorage: window.sessionStorage });
    if (firstTouch) payload.first_touch = firstTouch;
  } catch {}
  return compactValue(payload);
}

function buildPayload(metadata, payload) {
  const autoPayload = collectClientPayload();
  const raw = {
    ...(autoPayload && typeof autoPayload === "object" ? autoPayload : {}),
    ...(metadata && typeof metadata === "object" ? metadata : {}),
    ...(payload && typeof payload === "object" ? payload : {}),
  };
  const cleaned = compactValue(raw) || {};
  return Object.fromEntries(
    Object.entries(cleaned).filter(([key]) => !RESERVED_PAYLOAD_KEYS.has(String(key).toLowerCase()))
  );
}

function compactString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export async function submitDemoRequest({
  phone,
  phoneNumber,
  email,
  firstName,
  lastName,
  fullName,
  customerInfo,
  requestType = "demo",
  metadata,
  payload,
} = {}) {
  const privacyRequest = isPrivacyRequestType(requestType);
  const incomingCustomerInfo = mergeCustomerInfo(customerInfo);
  const resolvedPhone =
    compactString(typeof phoneNumber === "string" ? phoneNumber : phone) ?? incomingCustomerInfo.phone_number;
  const resolvedEmail = normalizeEmail(email) ?? normalizeEmail(incomingCustomerInfo.email);
  const resolvedFirstName = compactString(firstName) ?? incomingCustomerInfo.first_name ?? null;
  const resolvedLastName = compactString(lastName) ?? incomingCustomerInfo.last_name ?? null;
  const resolvedFullName = compactString(fullName) ?? incomingCustomerInfo.full_name ?? null;

  const emailError = resolvedEmail || privacyRequest ? getEmailError(resolvedEmail) : null;
  if (emailError) {
    const err = new Error("Invalid email");
    err.code = emailError;
    err.validation = { email: emailError };
    throw err;
  }
  if (!resolvedPhone && !resolvedEmail) {
    const err = new Error("Phone or email required");
    err.code = "contact_required";
    err.validation = { contact: "Phone or email required" };
    throw err;
  }
  const currentUrl = resolveCurrentUrl(metadata, payload);
  const userAgent = resolveUserAgent(metadata, payload);
  const visitorId = privacyRequest ? null : getVisitorId();
  const payloadData = privacyRequest ? compactValue(payload) || {} : buildPayload(metadata, payload);
  const normalizedCustomerInfo = mergeCustomerInfo(incomingCustomerInfo, {
    visitor_id: visitorId,
    phone_number: resolvedPhone || null,
    email: resolvedEmail || null,
    first_name: resolvedFirstName,
    last_name: resolvedLastName,
    full_name: resolvedFullName,
  });
  const body = {
    request_type: typeof requestType === "string" && requestType.trim() ? requestType.trim() : "demo",
    customer_info: normalizedCustomerInfo,
    metadata: {
      current_url: currentUrl,
      user_agent: userAgent,
    },
    payload: payloadData,
  };
  const timeout = createTimeoutSignal(22000);
  try {
    const res = await fetch(BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: timeout.signal,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || data?.ok !== true) {
      const err = new Error(data?.error || `Request failed (${res.status})`);
      if (data?.errors) err.validation = data.errors;
      err.code = data?.code;
      err.status = res.status;
      throw err;
    }
    try {
      if (!privacyRequest) {
      const event = buildLeadEvent({
        requestType: body.request_type,
        source: payloadData?.source,
        requestId: data.id,
        pagePath: typeof window !== "undefined" ? window.location?.pathname : null,
        firstTouch: payloadData?.first_touch,
      });
      trackEvent(event.name, event.params);
      }
    } catch {}
    return data;
  } finally {
    timeout.clear();
  }
}

export async function listDemoRequests({ limit = 100, offset = 0 } = {}) {
  const res = await fetch(`${BASE}?limit=${limit}&offset=${offset}`);
  if (!res.ok) throw new Error(`List failed (${res.status})`);
  return res.json();
}

